import { extraSignGlyph } from '@heraion/shared';
import type { TimelineRow } from './timelineRows';
import { formatWhen } from '../primitives/formatWhen';

// The timeline feed's TEXT layer — every string a row prints, and the
// screen-reader mirror of the ones that contain a symbol. Split out of
// TimelineList (Track R rule: the 500-line cap forced the cut when IB-9 gave
// bodyweight rows a load) so the renderer holds layout and this holds wording.
//
// Built as a factory over (t, locale) rather than free functions: every
// formatter here needs one or both, and threading them through six call sites
// is the noise the closure removes. Pure — no React, no theme, so the wording
// rules are testable without rendering a tree.
//
// `t` is typed structurally on purpose: this module needs "a lookup that
// returns a string", not a dependency on i18next's generics.
type Translate = (key: string, opts?: Record<string, unknown>) => string;

export type TimelineRowText = {
  /** HH:mm in the athlete's locale; '' when the stamp is unparseable. */
  timeFmt: (iso: string) => string;
  /** Locale-grouped integer tonnage for the per-set Σ chip. */
  volFmt: (n: number) => string;
  /** The row's reps · load line (or duration/distance for timed/cardio). */
  summaryFor: (row: TimelineRow) => string;
  /** Words-only spoken form, or undefined when summaryFor holds no symbol. */
  summaryA11yFor: (row: TimelineRow) => string | undefined;
};

export function makeTimelineRowText(
  t: Translate,
  locale: string,
): TimelineRowText {
  const timeFmt = (iso: string): string => formatWhen(iso, locale, { time: true });

  // mm:ss-ish hold/cardio duration from a draft-string seconds value.
  const formatSecs = (raw: string): string | null => {
    if (!raw.trim()) return null;
    const secs = parseInt(raw, 10);
    if (!Number.isFinite(secs)) return null;
    const m = Math.floor(secs / 60);
    const sec = secs % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  // Locale-grouped integer tonnage for the per-set volume chip (Antenore
  // 2026-06-19). Rounded — the chip is a sanity-check glance, not an exact
  // figure; the analytics path keeps full precision.
  const volFmt = (n: number): string => {
    try {
      return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return String(Math.round(n));
    }
  };

  // The load this row PRINTS, or null when there is none to show. A BODYWEIGHT
  // row prices its own load (bw×pct + signed extra, already folded in) because
  // its `weight` field carries only the EXTRA — blank on a plain push-up, which
  // is why the feed used to read "10 reps" and nothing else (IB-9). Every other
  // kind shows the typed magnitude with its assistance sign. Single source for
  // both the visible summary and its screen-reader mirror, so the two can't
  // disagree about what the athlete is being told.
  const loadTextFor = (row: TimelineRow): string | null => {
    if (row.kind === 'bodyweight') {
      return row.bodyweightLoad == null ? null : volFmt(row.bodyweightLoad);
    }
    return row.weight.trim()
      ? `${row.extraNegative ? extraSignGlyph(true) : ''}${row.weight}`
      : null;
  };

  // Per-implement/per-side entry reads as «2× 20 kg» so the convention (log ONE
  // dumbbell, the app counts both) is visible in the number itself — the volume
  // chip alone wasn't enough to prevent IB-8. Suppressed on dropset rows: their
  // breakdown already uses «10×80» (reps×weight), and two unrelated meanings of
  // × stacked on one card read ambiguously (ux-a11y audit 2026-07-14).
  const showsSides = (row: TimelineRow): boolean =>
    row.loadedSides === 2 && row.dropsetVariant === null;

  const summaryFor = (row: TimelineRow): string => {
    if (row.kind === 'timed') {
      return formatSecs(row.duration) ?? '—';
    }
    if (row.kind === 'cardio') {
      const parts: string[] = [];
      const dur = formatSecs(row.duration);
      if (dur) parts.push(dur);
      if (row.distanceM.trim()) parts.push(`${row.distanceM} m`);
      return parts.length ? parts.join(' · ') : '—';
    }
    const reps = row.reps.trim() || '—';
    const load = loadTextFor(row);
    if (load === null) return `${reps} ${t('workout.reps')}`;
    const sides = showsSides(row) ? '2× ' : '';
    return `${reps} ${t('workout.reps')} · ${sides}${load} ${row.unit}`;
  };

  // Words-only screen-reader mirror of summaryFor: the visible «2×» glyph must
  // never be vocalised raw (same convention as the Σ volume chip and
  // DropsetSummary — symbols get an explicit spoken phrase).
  const summaryA11yFor = (row: TimelineRow): string | undefined => {
    const load = loadTextFor(row);
    if (
      row.kind === 'timed' ||
      row.kind === 'cardio' ||
      load === null ||
      !showsSides(row)
    ) {
      return undefined;
    }
    return t('mobile.timeline.setSummaryBothSidesA11y', {
      reps: row.reps.trim() || '—',
      weight: load,
      unit: row.unit,
      defaultValue:
        '{{reps}} reps at {{weight}} {{unit}} per side, both sides counted',
    });
  };

  return { timeFmt, volFmt, summaryFor, summaryA11yFor };
}
